/**
 * Palette de fonds pour l'image partagée.
 *
 * Tous les dégradés sont sombres et désaturés : le texte est toujours blanc,
 * la lisibilité est donc garantie quel que soit le tirage, sans avoir à tester
 * chaque combinaison. La catégorie de l'événement ajoute une teinte discrète
 * par-dessus (halo radial), pour l'identité sans jamais casser le contraste.
 */

import type { EventKind } from '@/types/database';

export interface ShareGradient {
  id: string;
  /** Angle du dégradé linéaire, en degrés (0 = vers la droite, 90 = vers le bas). */
  angle: number;
  /** Arrêts couleur, du départ à l'arrivée. Volontairement sombres. */
  stops: string[];
}

// Une douzaine de dégradés profonds, dérivés de la palette LaFête (encre-nuit,
// prune, velours, vieil-or, basilic…) mais assombris pour rester sous le texte.
export const SHARE_GRADIENTS: ShareGradient[] = [
  { id: 'nuit', angle: 145, stops: ['#16233D', '#0C1526'] },
  { id: 'velours', angle: 150, stops: ['#3A161C', '#1B0E12'] },
  { id: 'prune', angle: 160, stops: ['#2C1A42', '#160E24'] },
  { id: 'foret', angle: 155, stops: ['#173026', '#0D1A14'] },
  { id: 'terre', angle: 150, stops: ['#3A1E12', '#1C0F0A'] },
  { id: 'ardoise', angle: 165, stops: ['#242A33', '#12151B'] },
  { id: 'bordeaux', angle: 150, stops: ['#3E1A2A', '#1A0C13'] },
  { id: 'olive', angle: 158, stops: ['#2C2E16', '#15160B'] },
  { id: 'encre', angle: 140, stops: ['#1D2540', '#120C22'] },
  { id: 'cacao', angle: 152, stops: ['#2E2013', '#160F0A'] },
  { id: 'sapin', angle: 162, stops: ['#12291F', '#0E1B2A'] },
  { id: 'aubergine', angle: 148, stops: ['#301A34', '#161022'] },
];

// Accent par catégorie pour le halo — teintes vives mais posées à faible opacité.
const CATEGORY_ACCENT: Record<EventKind, string> = {
  market: '#E06A3A',
  family: '#5E8A63',
  cultural: '#7A54A6',
  music: '#3A6DB0',
  fair: '#B98A46',
  funfair: '#D4536A',
  circus: '#B0505A',
  food: '#E0863A',
  sport: '#4A78B0',
  motorsport: '#C85060',
  firework: '#C86A8A',
  other: '#B99A6A',
};

/** Couleur d'accent (halo + pastilles) associée à la catégorie. */
export function categoryAccent(kind: EventKind): string {
  return CATEGORY_ACCENT[kind] ?? CATEGORY_ACCENT.other;
}

export type BackgroundChoice =
  | { kind: 'photo' }
  | { kind: 'gradient'; gradient: ShareGradient };

/**
 * Nombre de fonds proposés : les dégradés, plus la photo de l'événement quand
 * elle existe (elle occupe alors le premier index, montrée en priorité).
 */
export function backgroundOptionCount(hasPhoto: boolean): number {
  return SHARE_GRADIENTS.length + (hasPhoto ? 1 : 0);
}

/** Traduit un index de fond en photo ou dégradé concret. */
export function resolveBackground(index: number, hasPhoto: boolean): BackgroundChoice {
  if (hasPhoto && index === 0) return { kind: 'photo' };
  const offset = hasPhoto ? 1 : 0;
  const n = SHARE_GRADIENTS.length;
  const i = (((index - offset) % n) + n) % n;
  return { kind: 'gradient', gradient: SHARE_GRADIENTS[i] };
}

/**
 * Tire un index de fond différent de l'actuel. Sans ce « jamais le même », le
 * bouton « Autre fond » pourrait retomber sur le fond affiché et sembler cassé.
 */
export function nextBackgroundIndex(
  current: number | null,
  count: number,
  random: () => number = Math.random,
): number {
  if (count <= 1) return 0;
  if (current == null) return Math.min(count - 1, Math.floor(random() * count));
  const roll = Math.min(count - 2, Math.floor(random() * (count - 1)));
  return roll >= current ? roll + 1 : roll;
}
